import { useEffect, useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { offlineQueue, QueuedMutation } from '@/services/offlineQueue'
import { apiClient } from '@/services/api/client'
import { useNetworkStatus } from './useNetworkStatus'

interface SyncStatus {
  isSyncing: boolean
  queuedCount: number
  lastSyncTime: number | null
  syncErrors: Array<{ mutation: QueuedMutation; error: string }>
}

export function useOfflineSync() {
  const queryClient = useQueryClient()
  const { isOnline } = useNetworkStatus()
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    isSyncing: false,
    queuedCount: 0,
    lastSyncTime: null,
    syncErrors: [],
  })

  /**
   * Update queued count
   */
  const updateQueuedCount = useCallback(async () => {
    const count = await offlineQueue.getCount()
    setSyncStatus(prev => ({ ...prev, queuedCount: count }))
  }, [])

  /**
   * Sync a single mutation
   */
  const syncMutation = async (mutation: QueuedMutation): Promise<void> => {
    try {
      console.log('[OfflineSync] Syncing mutation:', mutation)

      await apiClient.request({
        url: mutation.endpoint,
        method: mutation.method,
        data: mutation.data,
      })

      // Remove from queue on success
      await offlineQueue.removeMutation(mutation.id)

      // Invalidate relevant queries based on resource type
      switch (mutation.resource) {
        case 'aircraft':
          queryClient.invalidateQueries({ queryKey: ['aircraft-list'] })
          queryClient.invalidateQueries({ queryKey: ['aircraft', mutation.resourceId] })
          break
        case 'journals':
          queryClient.invalidateQueries({ queryKey: ['journals-list'] })
          queryClient.invalidateQueries({ queryKey: ['journal', mutation.resourceId] })
          queryClient.invalidateQueries({ queryKey: ['aircraft-list'] }) // Aircraft may have updated journal status
          break
        case 'flight-journals':
          queryClient.invalidateQueries({ queryKey: ['journals-list'] })
          queryClient.invalidateQueries({ queryKey: ['journal', mutation.resourceId] })
          queryClient.invalidateQueries({ queryKey: ['aircraft-list'] }) // Aircraft may have updated data
          break
        default:
          // Invalidate all queries as fallback
          queryClient.invalidateQueries()
      }

      console.log('[OfflineSync] Mutation synced successfully:', mutation.id)
    } catch (error: any) {
      console.error('[OfflineSync] Failed to sync mutation:', mutation.id, error)

      const errorMessage = error.response?.data?.message || error.message || 'Unknown error'

      // Increment retry count
      await offlineQueue.incrementRetryCount(mutation.id, errorMessage)

      // Check if we should still retry
      if (!offlineQueue.shouldRetry({ ...mutation, retryCount: mutation.retryCount + 1 })) {
        console.warn('[OfflineSync] Max retries exceeded for mutation:', mutation.id)
        throw new Error(`Max retries exceeded: ${errorMessage}`)
      } else {
        throw error
      }
    }
  }

  /**
   * Sync all queued mutations
   */
  const syncQueue = useCallback(async () => {
    if (!isOnline) {
      console.log('[OfflineSync] Cannot sync: offline')
      return
    }

    setSyncStatus(prev => ({ ...prev, isSyncing: true, syncErrors: [] }))

    try {
      const mutations = await offlineQueue.getAllMutations()

      if (mutations.length === 0) {
        console.log('[OfflineSync] No mutations to sync')
        setSyncStatus(prev => ({
          ...prev,
          isSyncing: false,
          queuedCount: 0,
          lastSyncTime: Date.now()
        }))
        return
      }

      console.log(`[OfflineSync] Syncing ${mutations.length} mutations...`)

      const errors: Array<{ mutation: QueuedMutation; error: string }> = []

      // Sync mutations sequentially to maintain order
      for (const mutation of mutations) {
        try {
          await syncMutation(mutation)
        } catch (error: any) {
          errors.push({
            mutation,
            error: error.message || 'Sync failed',
          })
        }
      }

      const remainingCount = await offlineQueue.getCount()

      setSyncStatus({
        isSyncing: false,
        queuedCount: remainingCount,
        lastSyncTime: Date.now(),
        syncErrors: errors,
      })

      if (errors.length === 0) {
        console.log('[OfflineSync] All mutations synced successfully')
      } else {
        console.warn(`[OfflineSync] Sync completed with ${errors.length} errors`)
      }
    } catch (error) {
      console.error('[OfflineSync] Sync failed:', error)
      setSyncStatus(prev => ({ ...prev, isSyncing: false }))
    }
  }, [isOnline, queryClient])

  /**
   * Manually trigger sync
   */
  const triggerSync = useCallback(() => {
    syncQueue()
  }, [syncQueue])

  /**
   * Clear all failed mutations
   */
  const clearFailedMutations = useCallback(async () => {
    await offlineQueue.clearFailedMutations()
    await updateQueuedCount()
  }, [updateQueuedCount])

  // Sync when coming online
  useEffect(() => {
    if (isOnline) {
      console.log('[OfflineSync] Network online, triggering sync...')
      syncQueue()
    }
  }, [isOnline, syncQueue])

  // Update queued count on mount and periodically
  useEffect(() => {
    updateQueuedCount()

    const interval = setInterval(updateQueuedCount, 5000) // Update every 5 seconds

    return () => clearInterval(interval)
  }, [updateQueuedCount])

  // Sync on mount if online
  useEffect(() => {
    if (isOnline) {
      syncQueue()
    }
  }, []) // Only run once on mount

  return {
    ...syncStatus,
    isOnline,
    triggerSync,
    clearFailedMutations,
  }
}
